import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';
import {toast} from 'react-toastify';
import {MultiSelect} from '../../selects/MultiSelect';
import {Switch} from '../../buttons/Swtich';
import {InformesApi} from '../../../api/informesApi';
import {notifyOnboardingProgress} from '../../../utils/onboardingProgress';
import {selectableClients} from '../state/clientList';

/**
 * «Duplicar en otro cliente…» (patrón de `DuplicateViewModal` de las vistas): el mismo informe
 * —nombre, período, comparación, filtros, modo y la hoja entera— en uno o varios clientes.
 * Las cuentas NO se copian (son del cliente de origen): el informe nuevo nace con «todas las
 * cuentas» de su cliente, igual que al cambiar de cliente en el editor.
 *
 * Crea un informe por cliente, en secuencia; con «Asociar al portal» además lo publica.
 * `onDuplicated(created)` con los informes creados.
 */
export function DuplicateInformeModal({isOpen, toggle, informe, settings, layout, templateId, agencyClients, currentClientId, onDuplicated}) {
    const {t} = useTranslation();
    const [selectedIds, setSelectedIds] = useState([]);
    const [publish, setPublish] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => { if (isOpen) { setSelectedIds([]); setPublish(false); } }, [isOpen]);

    const eligible = useMemo(
        () => selectableClients(agencyClients).filter((c) => String(c.id) !== String(currentClientId)),
        [agencyClients, currentClientId],
    );

    const duplicate = async () => {
        if (!informe || selectedIds.length === 0) return;
        setSaving(true);
        const created = [];
        const failed = [];
        for (const clientId of selectedIds) {
            const payload = {
                client_id: clientId,
                name: informe.name,
                mode: informe.mode,
                settings: {...settings, selected_items: []},
                layout,
                template_id: templateId || null,
            };
            // eslint-disable-next-line no-await-in-loop
            const r = await InformesApi.create(payload);
            if (!r || r.status !== 'OK') { failed.push(clientId); continue; }
            // eslint-disable-next-line no-await-in-loop
            const published = publish ? await InformesApi.publish(r.data.id) : null;
            if (publish && (!published || published.status !== 'OK')) { failed.push(clientId); continue; }
            created.push(r.data);
        }
        setSaving(false);
        // Duplicar un informe migrado desde el Explorador es el primero «armado»: el drawer se tilda
        // sin recargar. Un aviso por tanda, no por cliente.
        if (created.length) notifyOnboardingProgress({status: 'OK'});
        if (created.length) toast.success(t('informes.duplicate.success', {count: created.length, defaultValue: 'Informe duplicado en {{count}} clientes.'}));
        if (failed.length) toast.error(t('informes.duplicate.partial_error', {count: failed.length, defaultValue: 'No se pudo duplicar en {{count}} clientes.'}));
        if (onDuplicated) onDuplicated(created);
        if (!failed.length) toggle();
    };

    if (!informe) return null;
    return (
        <Modal isOpen={isOpen} toggle={saving ? undefined : toggle} size="md" data-testid="duplicate-informe-modal">
            <ModalHeader toggle={saving ? undefined : toggle}>{t('informes.duplicate.title', 'Duplicar en otro cliente')}</ModalHeader>
            <ModalBody>
                <div className="modal-section inf-save">
                    <p className="inf-modal-lead">
                        {t('informes.duplicate.source', 'Se duplica')} <strong>{informe.name}</strong>. {t('informes.duplicate.lead', 'Se copian el período, la comparación, los filtros y la hoja entera; las cuentas se eligen en cada cliente.')}
                    </p>
                    {eligible.length === 0
                        ? <p className="inf-fields-empty">{t('informes.duplicate.no_clients', 'No hay otros clientes en los que duplicarlo.')}</p>
                        : (
                            <div className="form-row">
                                <MultiSelect
                                    key={informe.id}
                                    items={eligible}
                                    selectedItemIds={selectedIds}
                                    onChange={setSelectedIds}
                                    itemDataMap={(c) => ({name: c.name || t('informes.duplicate.unnamed_client', 'Cliente sin nombre'), id: String(c.id), isActive: true})}
                                    showOnlyActiveCheck={false}
                                />
                            </div>
                        )}
                    <div className="form-row switch-input">
                        <div className="label-with-explanation">
                            <label>{t('informes.duplicate.publish_label', 'Asociar al portal de cada cliente')}</label>
                            <p>{t('informes.duplicate.publish_hint', 'El informe aparece en el portal apenas se crea. Si es una foto, se genera en unos minutos.')}</p>
                        </div>
                        <Switch name="duplicatePublish" checked={publish} onChange={(e) => setPublish(e.target.checked)}
                                style={{margin: 0}} disabled={saving} messageToAlertUserIfChecked={null}/>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <button type="button" className="btn -alt -negative" onClick={toggle} disabled={saving}>{t('common.cancel', 'Cancelar')}</button>
                <button type="button" className="btn -alt" onClick={duplicate} disabled={saving || selectedIds.length === 0} data-testid="confirm-duplicate">
                    {saving ? t('common.saving', 'Guardando…') : t('informes.duplicate.confirm', 'Duplicar')}
                </button>
            </ModalFooter>
        </Modal>
    );
}
